import { sanitizeSVG } from '@goldcast/utils';
import { IMAGE_FORMATS, VIDEO_FORMATS, AUDIO_FORMATS } from './constants';
import { AssetType, SanitizedFileResult, FileValidationError } from './types';
import appLogger from '@/services/logger';
import { getTotalVideoDuration } from '@/libs/getVideoDuration';
import { getTotalAudioDuration } from '@/libs/getAudioDuration';
import { getFileSizeInUnit } from '@/libs/file';

export const getAssetType = (filename: string, formats: string[]): AssetType | null => {
  const extension = filename.split('.').pop()?.toLowerCase();
  if (!extension || !formats.includes(extension)) {
    return null;
  }

  // Currently returns 'image' for supported image formats.
  // To support other media types, extend this logic:
  if (IMAGE_FORMATS.includes(extension)) {
    return 'image';
  }

  if (VIDEO_FORMATS.includes(extension)) {
    return 'video';
  }

  if (AUDIO_FORMATS.includes(extension)) {
    return 'audio';
  }

  return null;
};

export const sanitizeFiles = async (
  files: File[],
  acceptedFormats: string[],
  maxSize: number,
  maxIndividualFileSize?: number
): Promise<SanitizedFileResult> => {
  const validFiles: { file: File; type: AssetType; duration?: number }[] = [];
  const invalidFiles: FileValidationError[] = [];
  let totalSize = 0;

  // First pass: validate individual files and calculate total size
  for (const file of files) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const assetType = getAssetType(file.name, acceptedFormats);

    // Check file format
    if (!ext || !acceptedFormats.includes(ext)) {
      invalidFiles.push({
        file,
        reason: 'INVALID_FORMAT',
        message: `File "${file.name}" has an unsupported format. Accepted formats: ${acceptedFormats.join(', ')}`
      });
      continue;
    }

    // Check asset type
    if (!assetType) {
      invalidFiles.push({
        file,
        reason: 'INVALID_ASSET_TYPE',
        message: `File "${file.name}" is not a valid asset type`
      });
      continue;
    }

    // Check individual file size limit
    const individualFileSizeLimit = maxIndividualFileSize || maxSize;
    if (file.size > individualFileSizeLimit) {
      invalidFiles.push({
        file,
        reason: 'FILE_TOO_LARGE',
        message: `Can't upload "${file.name}" (${Math.round(parseFloat(getFileSizeInUnit(file.size, 'MB')))} MB), since it exceeds the upload limit of ${Math.round(parseFloat(getFileSizeInUnit(individualFileSizeLimit, 'MB')))} MB`
      });
      continue;
    }

    totalSize += file.size;
  }

  for (const file of files) {
    if (invalidFiles.some(error => error.file === file)) {
      continue;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    const assetType = getAssetType(file.name, acceptedFormats);

    if (totalSize > maxSize) {
      invalidFiles.push({
        file,
        reason: 'TOTAL_SIZE_EXCEEDED',
        message: `Adding "${file.name}" would exceed the total size limit of ${getFileSizeInUnit(maxSize, 'MB')} MB`
      });
      continue;
    }

    // Process SVG files
    let processedFile = file;
    if (ext === 'svg') {
      try {
        const fileContent = await file.text();
        const sanitizedContent = sanitizeSVG(fileContent, {
          ADD_TAGS: ['svg']
        });
        processedFile = new File([sanitizedContent], file.name, { type: file.type });
      } catch (error) {
        invalidFiles.push({
          file,
          reason: 'INVALID_FORMAT',
          message: `Failed to process SVG file "${file.name}": ${error instanceof Error ? error.message : 'Unknown error'}`
        });
        continue;
      }
    }

    let duration: number | undefined;
    if (assetType === 'video') {
      try {
        duration = await getTotalVideoDuration([processedFile]);
      } catch (error) {
        appLogger.warn('Could not get duration for video', error);
      }
    } else if (assetType === 'audio') {
      try {
        duration = await getTotalAudioDuration([processedFile]);
      } catch (error) {
        appLogger.warn('Could not get duration for audio', error);
      }
    }

    validFiles.push({ file: processedFile, type: assetType!, duration });
  }

  return {
    validFiles,
    invalidFiles,
    totalSize,
    maxSize
  };
};
